import { Caption1, makeStyles, mergeClasses, shorthands, tokens } from "@fluentui/react-components";
import { ChevronDown16Regular, ChevronRight16Regular } from "@fluentui/react-icons";
import { PersonAvatar } from "@/features/access/PersonAvatar";
import { useDataTableStyles } from "@/components/data-table";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { Fragment, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { CompactKpi } from "@/components/compact-kpi";
import type { EmploymentType, OrganizationTeam, Project, SolutionCategory, UserResource } from "@/engine/types";
import { formatDate } from "@/lib/date-format";
import { fmtCurrency, fmtMM } from "@/lib/format";
import {
  aggregateMonthlyToQuarterly,
  assignmentLaborCost,
  assignmentMonthMM,
  attentionProjectIds,
  distinctQuarters,
  overloadedResourceIds,
  personPlanSlices,
  portfolioMonths,
  projectCostSummary,
  projectMonthlyBuckets,
  quarterOf,
  quarterStartMonth,
  rateOf,
  sumRows,
  type GroupRow,
  type ProjectCostSummary,
  type YearMonth,
} from "./portfolio";
import { EMPLOYMENT_LABEL, SOLUTION_CATEGORY_LABEL } from "./resource-meta";
import { TeamMark } from "./team-appearance";
import { StatRibbon } from "@/components/stat-ribbon";
import { ReadinessMeter } from "@/components/visual/ink-readiness";
import { todayISO } from "@/engine/date";
import { portfolioReadiness } from "./portfolio-readiness";

/* ------------------------------------------------------------------------------------------------
 * Shared table chrome
 * ---------------------------------------------------------------------------------------------- */

/** Fixed width (px) for monthly M/M matrix columns — fits `26.08` / `0.0` in tabular nums. */
export const MM_MONTH_COL_WIDTH = 56;
/** Fixed width (px) for quarterly M/M columns — fits `26 Q3` / `00.0`. */
export const MM_QUARTER_COL_WIDTH = 64;
/** Compact meta columns; the 플랜/그룹 column takes the remaining width. */
export const MM_COUNT_COL_WIDTH = 72;
export const MM_PLANNED_COL_WIDTH = 96;
export const MM_LABOR_COL_WIDTH = 124;
export const MM_SHARE_COL_WIDTH = 64;
/** 플랜 표의 진척 막대. 비율과 색이 상태이고, 판정 단어는 두지 않는다. */
export const MM_READY_COL_WIDTH = 112;

export type TimeScale = "month" | "quarter";

const usePortfolioLocalStyles = makeStyles({
  stack: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
  },
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalM,
  },
  kpiInner: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM} ${tokens.spacingVerticalM}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    flexWrap: "wrap",
    marginBottom: tokens.spacingVerticalM,
  },
  toolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  /**
   * M/M 월별 매트릭스.
   * tableLayout: fixed 로 M/M 열 폭을 고정하고, 첫 번째 플랜 열이 남는 폭을 흡수한다.
   * minWidth 는 컬럼 합산으로 지정하여 컨테이너보다 좁아져도 플랜 열이 찌그러지지 않고 가로 스크롤된다.
   */
  matrixTable: {
    tableLayout: "fixed",
    width: "100%",
  },
  /** Header typography — same size/weight as body. Chrome (bg, muted color) stays. */
  header: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
  },
  month: {
    textAlign: "center",
    minWidth: "56px",
    fontVariantNumeric: "tabular-nums",
  },
  /** Fixed-width monthly M/M column (header + cell). Heatmap is applied separately on data cells. */
  mmCol: {
    textAlign: "center",
    width: `${MM_MONTH_COL_WIDTH}px`,
    minWidth: `${MM_MONTH_COL_WIDTH}px`,
    maxWidth: `${MM_MONTH_COL_WIDTH}px`,
    padding: `6px ${tokens.spacingHorizontalXS}`,
    fontVariantNumeric: "tabular-nums",
    boxSizing: "border-box",
    overflow: "hidden",
    whiteSpace: "nowrap",
  },
  /** 플랜/그룹 컬럼 — 테이블 남은 너비를 모두 차지하며 좌측 틀 고정. */
  nameCol: {
    minWidth: "220px",
    width: "100%",
    position: "sticky",
    left: 0,
    zIndex: 1,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  nameHead: {
    zIndex: 3,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  nameTotal: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  rowLink: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":hover td": { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  readyCol: {
    width: `${MM_READY_COL_WIDTH}px`,
    minWidth: `${MM_READY_COL_WIDTH}px`,
    maxWidth: `${MM_READY_COL_WIDTH}px`,
    whiteSpace: "nowrap",
  },
  quiet: {
    color: tokens.colorNeutralForeground3,
  },
  scopeBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
    marginBottom: tokens.spacingVerticalS,
  },
  scopeBarLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  timeToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
    marginBottom: tokens.spacingVerticalS,
  },
  timeToolbarLeft: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexWrap: "wrap",
  },
  yearChips: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginLeft: tokens.spacingHorizontalXS,
  },
  yearChip: {
    padding: `2px ${tokens.spacingHorizontalS}`,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  yearChipActive: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderColor(tokens.colorNeutralStroke1),
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
    width: "100%",
    whiteSpace: "nowrap",
  },
  labelText: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: "1 1 auto",
  },
  expand: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    flexShrink: 0,
    padding: 0,
    border: "none",
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground2,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  expandSpacer: {
    width: "20px",
    height: "20px",
    flexShrink: 0,
  },
  /** 펼친 플랜 이름. 사람 글자 시작(펼침 20 + 간격 8 + 아바타 28 + 간격 8)과 맞춘다. */
  childLabel: {
    paddingLeft: "64px",
  },
  childRow: {
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "-2px",
    },
  },
  empty: {
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
  },
});

/** 목록 DataGrid와 같은 프레임·헤더·셀. 매트릭스 전용 폭·히트맵은 로컬 스타일. */
export function usePortfolioStyles() {
  const shared = useDataTableStyles();
  const local = usePortfolioLocalStyles();
  return {
    ...local,
    tableWrap: shared.frame,
    table: shared.table,
    fluent: shared.fluent,
    head: shared.head,
    headRow: shared.headRow,
    th: shared.th,
    td: shared.td,
    num: shared.num,
    total: shared.total,
    twoLine: shared.twoLine,
    clip: shared.clip,
    role: shared.role,
    identityCell: shared.identityCell,
    person: shared.person,
    personText: shared.personText,
    personName: shared.personName,
    nameLine: shared.nameLine,
    editCell: shared.editCell,
    editCellOverridden: shared.editCellOverridden,
  };
}

export interface KpiProps {
  title: string;
  value: ReactNode;
  hint?: ReactNode;
  unit?: ReactNode;
  tone?: "danger" | "warning" | "success" | "brand" | "muted";
  onClick?: () => void;
  className?: string;
}

/**
 * Theme-aware grayscale fill for monthly M/M *data* cells only.
 * Higher values mix a bit more foreground into the surface — never as dark as primary text.
 * Uses background-image so row hover/total backgrounds still show through.
 */
export function mmMonthCellStyle(value: number, maxValue: number): CSSProperties {
  if (value <= 0) return { color: tokens.colorNeutralForeground4 };
  if (maxValue <= 0) return { color: tokens.colorNeutralForeground3 };
  const t = Math.min(1, value / maxValue);
  const fillPct = Math.round(6 + t * 16); // 6–22%
  const tint = `color-mix(in srgb, ${tokens.colorNeutralForeground1} ${fillPct}%, transparent)`;
  const textMix = Math.round(t * 50);
  return {
    backgroundImage: `linear-gradient(${tint}, ${tint})`,
    color:
      textMix <= 0
        ? tokens.colorNeutralForeground3
        : `color-mix(in srgb, ${tokens.colorNeutralForeground3} ${100 - textMix}%, ${tokens.colorNeutralForeground2} ${textMix}%)`,
  };
}

export function Kpi({ title, value, hint, unit, tone, onClick, className }: KpiProps) {
  return (
    <CompactKpi
      label={title}
      value={value}
      hint={hint}
      unit={unit}
      tone={tone}
      onClick={onClick}
      className={className}
    />
  );
}

/* ------------------------------------------------------------------------------------------------
 * Team / people portfolio
 * ---------------------------------------------------------------------------------------------- */

export type PeopleScope = "all" | "over" | "multi";
export type PlanScope = "all" | "attention";

export type TeamGroupBy = "project" | "quarter" | "team" | "employment" | "person";

export const TEAM_GROUP_OPTIONS: Array<{ value: TeamGroupBy; label: string }> = [
  { value: "project", label: "플랜별" },
  { value: "quarter", label: "분기별" },
  { value: "team", label: "팀별" },
  { value: "employment", label: "고용형태별" },
  { value: "person", label: "인력별" },
];

export function PortfolioTeamView({
  projects,
  resources,
  teams,
  masked,
  defaultGroupBy = "project",
  groupBy: groupByProp,
  showKpis = true,
  onOpenResource,
  showMatrix = true,
  showBudget = false,
  query = "",
  peopleScope = "all",
  planScope = "all",
  timeScale = "month",
  yearFilter = "all",
}: {
  projects: Project[];
  resources: Record<string, UserResource>;
  teams: Record<string, OrganizationTeam>;
  masked?: boolean;
  defaultGroupBy?: TeamGroupBy;
  groupBy?: TeamGroupBy;
  showKpis?: boolean;
  onOpenResource?: (resourceId: string) => void;
  /** 월·분기 매트릭스. 팀 목록은 끈다. */
  showMatrix?: boolean;
  /** 플랜 줄에 소진·예산·집행률을 붙인다. */
  showBudget?: boolean;
  query?: string;
  peopleScope?: PeopleScope;
  planScope?: PlanScope;
  timeScale?: TimeScale;
  yearFilter?: string;
}) {
  const styles = usePortfolioStyles();
  const navigate = useNavigate();
  const [internalGroupBy] = useState<TeamGroupBy>(defaultGroupBy);
  const groupBy = groupByProp ?? internalGroupBy;
  const [openPeople, setOpenPeople] = useState<ReadonlySet<string>>(() => new Set());

  const months = useMemo(() => portfolioMonths(projects), [projects]);

  const model = useMemo(() => {
    type Acc = GroupRow & { monthly: number[]; people: Set<string>; projects: Set<string> };
    const acc = new Map<string, Acc>();
    const ensure = (key: string, label: string, extra: Partial<GroupRow> = {}): Acc => {
      let a = acc.get(key);
      if (!a) {
        a = { key, label, headcount: 0, plannedMM: 0, labor: 0, solution: 0, total: 0, monthly: months.map(() => 0), people: new Set(), projects: new Set(), ...extra };
        acc.set(key, a);
      }
      return a;
    };

    for (const p of projects) {
      if (groupBy === "project") ensure(p.id, p.name, { hint: `${(p.resourceAssignments ?? []).length}명` });
      for (const asg of p.resourceAssignments ?? []) {
        const res = resources[asg.resourceId];
        const labor = assignmentLaborCost(asg, res);
        const targets: Acc[] = [];
        if (groupBy === "project") targets.push(ensure(p.id, p.name, { hint: `${(p.resourceAssignments ?? []).length}명` }));
        else if (groupBy === "team") {
          const t = res?.teamId ? teams[res.teamId] : undefined;
          targets.push(ensure(t?.id ?? "__none", t?.name ?? "소속 없음", { color: t?.color, icon: t?.icon }));
        } else if (groupBy === "employment") {
          const e = (res?.employmentType ?? "permanent") as EmploymentType;
          targets.push(ensure(e, EMPLOYMENT_LABEL[e]));
        } else if (groupBy === "person") {
          targets.push(ensure(asg.resourceId, res?.displayName ?? "미지정", { hint: res?.roleTitle }));
        } else if (groupBy === "quarter") {
          // spread by month into quarters
          months.forEach((ym, i) => {
            const mm = assignmentMonthMM(asg, ym);
            if (mm <= 0) return;
            const q = ensure(quarterOf(ym), quarterOf(ym));
            q.plannedMM += mm;
            q.labor += mm * rateOf(asg, res);
            q.monthly[i]! += mm;
            q.people.add(asg.resourceId);
            q.projects.add(p.id);
          });
          continue;
        }
        for (const t of targets) {
          t.plannedMM += asg.plannedMM || 0;
          t.labor += labor;
          t.people.add(asg.resourceId);
          t.projects.add(p.id);
          months.forEach((ym, i) => (t.monthly[i]! += assignmentMonthMM(asg, ym)));
        }
      }
    }
    const slices = groupBy === "person" ? personPlanSlices(projects, resources, months) : null;
    const rows = [...acc.values()].map((a) => ({
      ...a,
      headcount: a.people.size,
      plannedMM: Math.round(a.plannedMM * 10) / 10,
      total: a.labor,
      projectCount: a.projects.size,
      plans: slices?.get(a.key) ?? [],
    }));
    rows.sort((a, b) => (groupBy === "quarter" ? a.key.localeCompare(b.key) : b.plannedMM - a.plannedMM));
    const monthTotals = months.map((_, i) => rows.reduce((s, r) => s + r.monthly[i]!, 0));
    return { rows, monthTotals };
  }, [projects, resources, teams, groupBy, months]);

  const total = useMemo(() => sumRows(model.rows), [model.rows]);
  const distinctPeople = useMemo(() => new Set(projects.flatMap((p) => (p.resourceAssignments ?? []).map((a) => a.resourceId))).size, [projects]);
  const overloadedIds = useMemo(() => overloadedResourceIds(projects), [projects]);
  const attention = useMemo(() => attentionProjectIds(projects, resources), [projects, resources]);
  const summaries = useMemo(() => {
    const map = new Map<string, ProjectCostSummary>();
    if (!showBudget) return map;
    for (const project of projects) map.set(project.id, projectCostSummary(project, resources));
    return map;
  }, [showBudget, projects, resources]);
  const q = query.trim().toLowerCase();
  const displayed = useMemo(() => {
    return model.rows.filter((row) => {
      if (q && !row.label.toLowerCase().includes(q)) return false;
      if (groupBy === "person" && peopleScope === "over" && !overloadedIds.has(row.key)) return false;
      if (groupBy === "person" && peopleScope === "multi" && row.projectCount <= 1) return false;
      if (groupBy === "project" && planScope === "attention" && !attention.has(row.key)) return false;
      return true;
    });
  }, [model.rows, q, groupBy, peopleScope, planScope, overloadedIds, attention]);
  const showReady = groupBy === "project";
  const readiness = useMemo(() => {
    if (!showReady) return null;
    const ids = new Set(displayed.map((row) => row.key));
    return portfolioReadiness(
      projects.filter((project) => ids.has(project.id)),
      todayISO(),
    );
  }, [showReady, displayed, projects]);
  const readyById = useMemo(() => new Map(readiness?.columns.map((column) => [column.key, column]) ?? []), [readiness]);
  const displayedMonths = useMemo(() => months.map((_, index) => displayed.reduce((sum, row) => sum + (row.monthly[index] ?? 0), 0)), [months, displayed]);
  const spendOf = (rows: typeof displayed) => {
    let spend = 0;
    let budget = 0;
    for (const row of rows) {
      const summary = summaries.get(row.key);
      if (!summary) continue;
      spend += summary.total;
      budget += summary.budget;
    }
    return { spend, budget };
  };
  const peakMonth = useMemo(() => {
    let best = -1;
    let idx = -1;
    model.monthTotals.forEach((v, i) => {
      if (v > best) {
        best = v;
        idx = i;
      }
    });
    return idx >= 0 ? { ym: months[idx]!, mm: best } : null;
  }, [model.monthTotals, months]);

  const showMonths = showMatrix && groupBy !== "quarter" && months.length > 0 && months.length <= 24;

  const filteredMonths = useMemo(() => {
    if (yearFilter === "all") return months;
    return months.filter((m) => m.startsWith(yearFilter));
  }, [months, yearFilter]);

  interface MatrixColDef {
    key: string;
    label: string;
  }

  const matrixCols = useMemo<MatrixColDef[]>(() => {
    if (timeScale === "quarter") {
      return distinctQuarters(filteredMonths).map((q) => ({
        key: q,
        label: formatDate(quarterStartMonth(q), "quarterShort"),
      }));
    }
    return filteredMonths.map((m) => ({
      key: m,
      label: formatDate(m, "yearMonthShort"),
    }));
  }, [timeScale, filteredMonths]);

  const getRowMMValues = (monthly: number[]): number[] => {
    const vals = filteredMonths.map((m) => {
      const idx = months.indexOf(m);
      return idx >= 0 ? monthly[idx] ?? 0 : 0;
    });
    if (timeScale === "quarter") {
      return aggregateMonthlyToQuarterly(filteredMonths, vals).values;
    }
    return vals;
  };

  const maxMatrixMM = useMemo(() => {
    let max = 0;
    for (const r of displayed) {
      const vals = getRowMMValues(r.monthly);
      for (const v of vals) if (v > max) max = v;
    }
    const totals = getRowMMValues(displayedMonths);
    for (const v of totals) if (v > max) max = v;
    return max;
  }, [displayed, displayedMonths, filteredMonths, timeScale]);

  const colWidth = timeScale === "quarter" ? MM_QUARTER_COL_WIDTH : MM_MONTH_COL_WIDTH;
  const moneyWidth = showBudget ? 120 + 120 + 72 : MM_SHARE_COL_WIDTH;
  const minTableWidth = showMonths
    ? 240 + (showReady ? MM_READY_COL_WIDTH : 0) + MM_COUNT_COL_WIDTH + MM_PLANNED_COL_WIDTH + (masked ? 0 : MM_LABOR_COL_WIDTH) + moneyWidth + matrixCols.length * colWidth
    : undefined;
  const shown = sumRows(displayed);
  const footerPeople = new Set<string>();
  const footerPlans = new Set<string>();
  for (const row of displayed) {
    row.people.forEach((id) => footerPeople.add(id));
    row.projects.forEach((id) => footerPlans.add(id));
  }
  let portfolioSpend = 0;
  let portfolioBudget = 0;
  for (const summary of summaries.values()) {
    portfolioSpend += summary.total;
    portfolioBudget += summary.budget;
  }
  const shownSpend = spendOf(displayed);
  const narrowed = q !== "" || peopleScope !== "all" || planScope !== "all";
  const emptyTitle = model.rows.length === 0
    ? "아직 배정이 없습니다"
    : groupBy === "person"
      ? "일치하는 사람이 없습니다"
      : groupBy === "team"
        ? "일치하는 팀이 없습니다"
        : "일치하는 플랜이 없습니다";
  const rowName = groupBy === "project" ? "플랜" : groupBy === "person" ? "인력" : groupBy === "team" ? "팀" : (TEAM_GROUP_OPTIONS.find((option) => option.value === groupBy)?.label.replace("별", "") ?? "이름");
  const burnLabel = (spend: number, budget: number) => (budget > 0 ? `${Math.round((spend / budget) * 100)}%` : "—");

  return (
    <div className={styles.stack}>
      {showKpis && projects.length > 0 && (
        <StatRibbon
          ariaLabel={showBudget ? "포트폴리오 플랜 요약" : "포트폴리오 인력 요약"}
          items={showBudget ? [
            { label: "플랜", value: `${projects.length}개`, hint: `투입 인력 ${distinctPeople}명` },
            { label: "전체 계획 M/M", value: fmtMM(total.plannedMM), hint: peakMonth ? `피크 ${formatDate(peakMonth.ym, "yearMonthShort")} · ${peakMonth.mm.toFixed(1)} M/M` : "배정 없음" },
            { label: "전체 예상 소진", value: fmtCurrency(portfolioSpend, masked), hint: portfolioBudget > 0 ? `집행률 ${Math.round((portfolioSpend / portfolioBudget) * 100)}%` : "승인 예산 없음" },
            { label: "주의", value: `${attention.size}개`, tone: attention.size > 0 ? "danger" : undefined, hint: attention.size > 0 ? "투입 초과 또는 예산 초과" : "넘는 플랜 없음" },
          ] : [
            { label: groupBy === "team" ? "팀" : "인력", value: groupBy === "team" ? `${model.rows.length}개` : `${model.rows.length}명`, hint: `플랜 ${projects.length}개` },
            { label: "전체 계획 M/M", value: fmtMM(total.plannedMM), hint: peakMonth ? `피크 ${formatDate(peakMonth.ym, "yearMonthShort")} · ${peakMonth.mm.toFixed(1)} M/M` : "배정 없음" },
            { label: "전체 인건비", value: fmtCurrency(total.labor, masked), hint: masked ? "금액이 가려져 있습니다" : `M/M당 평균 ${fmtCurrency(total.plannedMM ? Math.round(total.labor / total.plannedMM) : 0)}` },
            { label: "과부하", value: `${overloadedIds.size}명`, tone: overloadedIds.size > 0 ? "danger" : undefined, hint: overloadedIds.size > 0 ? "합산 투입률 100% 초과" : "과부하 없음" },
          ]}
        />
      )}

      {displayed.length === 0 ? (
        <GhostEmptyState
          title={emptyTitle}
          description={narrowed && model.rows.length > 0 ? "검색이나 범위를 바꿔 보세요." : "플랜에 사람을 배정하면 여기에 모입니다."}
        />
      ) : (
      <div className={styles.tableWrap}>
        <table
          className={mergeClasses(styles.table, showMonths && styles.matrixTable)}
          style={minTableWidth ? { minWidth: `${minTableWidth}px` } : undefined}
        >
          {showMonths && (
            <colgroup>
              <col />
              {showReady ? <col style={{ width: `${MM_READY_COL_WIDTH}px` }} /> : null}
              <col style={{ width: `${MM_COUNT_COL_WIDTH}px` }} />
              <col style={{ width: `${MM_PLANNED_COL_WIDTH}px` }} />
              {!masked && <col style={{ width: `${MM_LABOR_COL_WIDTH}px` }} />}
              {showBudget ? (
                <>
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "72px" }} />
                </>
              ) : (
                <col style={{ width: `${MM_SHARE_COL_WIDTH}px` }} />
              )}
              {matrixCols.map((c) => (
                <col key={c.key} style={{ width: `${colWidth}px` }} />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              <th className={mergeClasses(styles.th, styles.nameCol, styles.nameHead)}>{rowName}</th>
              {showReady ? <th className={mergeClasses(styles.th, styles.num, styles.readyCol)}>진척</th> : null}
              <th className={mergeClasses(styles.th, styles.num)}>{groupBy === "person" ? "플랜" : "인원"}</th>
              <th className={mergeClasses(styles.th, styles.num)}>계획 M/M</th>
              {!masked && <th className={mergeClasses(styles.th, styles.num)}>인건비</th>}
              {showBudget ? (
                <>
                  <th className={mergeClasses(styles.th, styles.num)}>예상 소진</th>
                  <th className={mergeClasses(styles.th, styles.num)}>승인 예산</th>
                  <th className={mergeClasses(styles.th, styles.num)}>집행률</th>
                </>
              ) : (
                <th className={mergeClasses(styles.th, styles.num)}>비중</th>
              )}
              {showMonths && matrixCols.map((c) => (
                <th key={c.key} className={mergeClasses(styles.th, styles.mmCol)} style={{ width: colWidth, minWidth: colWidth, maxWidth: colWidth }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayed.map((r) => {
              const share = shown.plannedMM ? Math.round((r.plannedMM / shown.plannedMM) * 100) : 0;
              const summary = summaries.get(r.key);
              const link = groupBy === "project";
              const ready = readyById.get(r.key);
              const rValues = showMonths ? getRowMMValues(r.monthly) : [];
              const expandable = groupBy === "person" && r.plans.length > 1;
              const open = expandable && openPeople.has(r.key);
              const openPlan = (projectId: string) => navigate(`/projects/${projectId}/team`);
              return (
                <Fragment key={r.key}>
                <tr className={link ? styles.rowLink : undefined} onClick={link ? () => openPlan(r.key) : undefined}>
                  <td className={mergeClasses(styles.td, styles.nameCol)}>
                    {groupBy === "person" ? (
                      <div
                        className={styles.label}
                        style={{
                          cursor: onOpenResource ? "pointer" : undefined,
                        }}
                        onClick={onOpenResource ? () => onOpenResource(r.key) : undefined}
                        title={onOpenResource ? "프로필 보기" : undefined}
                      >
                        {expandable ? (
                          <button
                            type="button"
                            className={styles.expand}
                            aria-expanded={open}
                            aria-label={open ? "플랜 접기" : "플랜 펼치기"}
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpenPeople((prev) => {
                                const next = new Set(prev);
                                if (next.has(r.key)) next.delete(r.key);
                                else next.add(r.key);
                                return next;
                              });
                            }}
                          >
                            {open ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                          </button>
                        ) : (
                          <span className={styles.expandSpacer} aria-hidden />
                        )}
                        <PersonAvatar name={r.label} size={28} />
                        <div className={mergeClasses(styles.labelText, styles.twoLine)}>
                          <span className={styles.clip} title={r.label}>{r.label}</span>
                          {r.hint && <Caption1 className={styles.role}>{r.hint}</Caption1>}
                        </div>
                      </div>
                    ) : (
                      <div className={styles.label}>
                        {(r.icon || r.color) && <TeamMark icon={r.icon} color={r.color} />}
                        <span className={styles.labelText} title={r.label}>
                          {r.label}
                        </span>
                        {r.hint && groupBy !== "project" && groupBy !== "team" ? <span style={{ color: tokens.colorNeutralForeground3 }}>{r.hint}</span> : null}
                      </div>
                    )}
                  </td>
                  {showReady ? (
                    <td className={mergeClasses(styles.td, styles.num, styles.readyCol)}>
                      {ready ? (
                        <ReadinessMeter value={ready.value} tone={ready.tone ?? "neutral"} />
                      ) : (
                        <span className={styles.quiet}>—</span>
                      )}
                    </td>
                  ) : null}
                  <td className={mergeClasses(styles.td, styles.num)}>{groupBy === "person" ? `${r.projectCount}개` : `${r.headcount}명`}</td>
                  <td className={mergeClasses(styles.td, styles.num)}>
                    {fmtMM(r.plannedMM)}
                  </td>
                  {!masked && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(r.labor)}</td>}
                  {showBudget ? (
                    <>
                      <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(summary?.total, masked)}</td>
                      <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(summary?.budget, masked)}</td>
                      <td className={mergeClasses(styles.td, styles.num)}>{summary ? burnLabel(summary.total, summary.budget) : "—"}</td>
                    </>
                  ) : (
                    <td className={mergeClasses(styles.td, styles.num)}>{share}%</td>
                  )}
                  {showMonths && rValues.map((v, i) => (
                    <td
                      key={matrixCols[i]!.key}
                      className={mergeClasses(styles.td, styles.mmCol)}
                      style={{ ...mmMonthCellStyle(v, maxMatrixMM), width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      {v > 0 ? v.toFixed(1) : "·"}
                    </td>
                  ))}
                </tr>
                {open && r.plans.map((plan) => {
                  const planShare = shown.plannedMM ? Math.round((plan.plannedMM / shown.plannedMM) * 100) : 0;
                  const planValues = showMonths ? getRowMMValues(plan.monthly) : [];
                  return (
                    <tr
                      key={`${r.key}:${plan.projectId}`}
                      className={mergeClasses(styles.rowLink, styles.childRow)}
                      tabIndex={0}
                      onClick={() => openPlan(plan.projectId)}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        openPlan(plan.projectId);
                      }}
                    >
                      <td className={mergeClasses(styles.td, styles.nameCol)}>
                        <div className={mergeClasses(styles.label, styles.childLabel)}>
                          <span className={styles.labelText} title={plan.projectName}>{plan.projectName}</span>
                        </div>
                      </td>
                      {showReady ? <td className={mergeClasses(styles.td, styles.num, styles.quiet)}>—</td> : null}
                      <td className={mergeClasses(styles.td, styles.num, styles.quiet)}>—</td>
                      <td className={mergeClasses(styles.td, styles.num)}>{fmtMM(plan.plannedMM)}</td>
                      {!masked && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(plan.labor)}</td>}
                      {showBudget ? (
                        <>
                          <td className={mergeClasses(styles.td, styles.num, styles.quiet)}>—</td>
                          <td className={mergeClasses(styles.td, styles.num, styles.quiet)}>—</td>
                          <td className={mergeClasses(styles.td, styles.num, styles.quiet)}>—</td>
                        </>
                      ) : (
                        <td className={mergeClasses(styles.td, styles.num)}>{planShare}%</td>
                      )}
                      {showMonths && planValues.map((v, i) => (
                        <td
                          key={matrixCols[i]!.key}
                          className={mergeClasses(styles.td, styles.mmCol)}
                          style={{ ...mmMonthCellStyle(v, maxMatrixMM), width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                        >
                          {v > 0 ? v.toFixed(1) : "·"}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                </Fragment>
              );
            })}
          </tbody>
          {displayed.length > 0 && (
            <tfoot>
              <tr className={styles.total}>
                <td className={mergeClasses(styles.td, styles.nameCol, styles.nameTotal)}>합계</td>
                {showReady ? <td className={mergeClasses(styles.td, styles.num, styles.quiet)}>—</td> : null}
                <td className={mergeClasses(styles.td, styles.num)}>{groupBy === "person" ? `${footerPlans.size}개` : `${footerPeople.size}명`}</td>
                <td className={mergeClasses(styles.td, styles.num)}>
                  {fmtMM(shown.plannedMM)}
                </td>
                {!masked && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(shown.labor)}</td>}
                {showBudget ? (
                  <>
                    <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(shownSpend.spend, masked)}</td>
                    <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(shownSpend.budget, masked)}</td>
                    <td className={mergeClasses(styles.td, styles.num)}>{burnLabel(shownSpend.spend, shownSpend.budget)}</td>
                  </>
                ) : (
                  <td className={mergeClasses(styles.td, styles.num)}>100%</td>
                )}
                {showMonths && (() => {
                  const totals = getRowMMValues(displayedMonths);
                  return totals.map((v, i) => (
                    <td
                      key={matrixCols[i]!.key}
                      className={mergeClasses(styles.td, styles.mmCol)}
                      style={{ ...mmMonthCellStyle(v, maxMatrixMM), width: colWidth, minWidth: colWidth, maxWidth: colWidth }}
                    >
                      {v > 0 ? v.toFixed(1) : "·"}
                    </td>
                  ));
                })()}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Budget portfolio
 * ---------------------------------------------------------------------------------------------- */

export type BudgetGroupBy = "project" | "quarter" | "costType" | "solutionCategory";

export const BUDGET_GROUP_OPTIONS: Array<{ value: BudgetGroupBy; label: string }> = [
  { value: "project", label: "플랜별" },
  { value: "quarter", label: "분기별" },
  { value: "costType", label: "비용 유형별" },
  { value: "solutionCategory", label: "솔루션 분류별" },
];

export function PortfolioBudgetView({
  projects,
  resources,
  masked,
  defaultGroupBy = "project",
  groupBy: groupByProp,
  showKpis = true,
}: {
  projects: Project[];
  resources: Record<string, UserResource>;
  masked?: boolean;
  defaultGroupBy?: BudgetGroupBy;
  groupBy?: BudgetGroupBy;
  showKpis?: boolean;
}) {
  const styles = usePortfolioStyles();
  const navigate = useNavigate();
  const [internalGroupBy] = useState<BudgetGroupBy>(defaultGroupBy);
  const groupBy = groupByProp ?? internalGroupBy;

  const summaries = useMemo(() => projects.map((p) => projectCostSummary(p, resources)), [projects, resources]);
  const agg = useMemo(
    () =>
      summaries.reduce(
        (a, s) => ({
          budget: a.budget + s.budget,
          total: a.total + s.total,
          labor: a.labor + s.directLabor,
          overhead: a.overhead + s.overhead,
          fee: a.fee + s.technicalFee,
          solution: a.solution + s.solution,
          expense: a.expense + s.expense,
          solutionCount: a.solutionCount + s.solutionCount,
        }),
        { budget: 0, total: 0, labor: 0, overhead: 0, fee: 0, solution: 0, expense: 0, solutionCount: 0 },
      ),
    [summaries],
  );
  const burn = agg.budget > 0 ? agg.total / agg.budget : 0;
  const overBudget = summaries.filter((s) => s.budget > 0 && s.total > s.budget).length;
  const months = useMemo(() => portfolioMonths(projects, { includeSolutions: true }), [projects]);

  type Row = { key: string; label: string; hint?: string; count?: string; amount: number; budget?: number; sub?: { labor: number; solution: number }; link?: string };
  const rows = useMemo<Row[]>(() => {
    if (groupBy === "project") {
      return summaries
        .map((s) => ({ key: s.projectId, label: s.projectName, hint: `${s.headcount}명 · ${fmtMM(s.plannedMM)} · 솔루션 ${s.solutionCount}개`, amount: s.total, budget: s.budget, sub: { labor: s.directLabor + s.overhead + s.technicalFee, solution: s.solution }, link: `/projects/${s.projectId}/budget` }))
        .sort((a, b) => b.amount - a.amount);
    }
    if (groupBy === "quarter") {
      const acc = new Map<string, { labor: number; solution: number; projects: Set<string> }>();
      for (const p of projects) {
        const o = p.budget?.costOptions;
        // load direct labor with 제경비·기술료 so quarters reconcile with the project totals
        const factor = (o?.includeOverhead ? o.overheadRatio : 1) * (1 + (o?.includeTechnicalFee ? o.technicalFeeRatio : 0));
        const buckets = projectMonthlyBuckets(p, resources, months);
        for (const b of buckets) {
          if (b.labor <= 0 && b.solution <= 0) continue;
          const q = quarterOf(b.ym as YearMonth);
          const a = acc.get(q) ?? { labor: 0, solution: 0, projects: new Set<string>() };
          a.labor += b.labor * factor;
          a.solution += b.solution;
          a.projects.add(p.id);
          acc.set(q, a);
        }
      }
      return [...acc.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([q, a]) => ({ key: q, label: formatDate(quarterStartMonth(q), "quarter"), hint: `활동 플랜 ${a.projects.size}개`, amount: a.labor + a.solution, sub: { labor: a.labor, solution: a.solution } }));
    }
    if (groupBy === "costType") {
      return [
        { key: "labor", label: "직접인건비", hint: "배정 M/M × 월 단가", amount: agg.labor },
        { key: "overhead", label: "제경비", hint: "직접인건비 × 제경비율", amount: agg.overhead },
        { key: "fee", label: "기술료", hint: "(인건비+제경비) × 기술료율", amount: agg.fee },
        { key: "solution", label: "솔루션·라이선스", hint: `${agg.solutionCount}개 품목`, amount: agg.solution },
        { key: "expense", label: "직접경비 (목표)", hint: "출장·교육·기타", amount: agg.expense },
      ].filter((r) => r.amount > 0 || r.key === "labor");
    }
    const cat = new Map<SolutionCategory, { amount: number; count: number }>();
    for (const p of projects) for (const s of p.solutions ?? []) {
      const c = cat.get(s.category) ?? { amount: 0, count: 0 };
      c.amount += s.totalCost;
      c.count += 1;
      cat.set(s.category, c);
    }
    return [...cat.entries()].sort(([, a], [, b]) => b.amount - a.amount).map(([k, v]) => ({ key: k, label: SOLUTION_CATEGORY_LABEL[k], hint: `${v.count}개 품목`, amount: v.amount }));
  }, [groupBy, summaries, projects, resources, months, agg]);

  const rowsTotal = rows.reduce((s, r) => s + r.amount, 0);
  const denominator = groupBy === "solutionCategory" ? agg.solution : groupBy === "project" ? agg.total : rowsTotal;
  const showBudget = groupBy === "project";
  const showSub = groupBy === "project" || groupBy === "quarter";

  return (
    <div className={styles.stack}>
      {showKpis && projects.length > 0 && (
        <StatRibbon
          ariaLabel="포트폴리오 예산 현황 요약"
          items={[
            {
              label: "전체 승인 예산",
              value: fmtCurrency(agg.budget, masked),
              hint: `${projects.length}개 플랜 합계`,
            },
            {
              label: "전체 예상 소진",
              value: fmtCurrency(agg.total, masked),
              tone: burn > 1 ? "danger" : undefined,
              hint: `집행률 ${Math.round(burn * 100)}%${burn > 1 ? " · 예산 초과" : ""}`,
            },
            {
              label: "전체 잔여 (마진)",
              value: fmtCurrency(agg.budget - agg.total, masked),
              tone: agg.budget - agg.total < 0 ? "danger" : "success",
              hint: overBudget > 0 ? `예산 초과 플랜 ${overBudget}개` : "모든 플랜 예산 내",
            },
            {
              label: "솔루션·라이선스",
              value: fmtCurrency(agg.solution, masked),
              hint: `${agg.solutionCount}개 품목 · 소진의 ${agg.total ? Math.round((agg.solution / agg.total) * 100) : 0}%`,
            },
          ]}
        />
      )}

      {rows.length === 0 ? (
        <GhostEmptyState title="표시할 비용이 없습니다" description="플랜에 예산이나 배정이 생기면 여기에 모입니다." />
      ) : (
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={mergeClasses(styles.th, styles.nameCol, styles.nameHead)}>{groupBy === "costType" ? "유형" : (BUDGET_GROUP_OPTIONS.find((o) => o.value === groupBy)?.label.replace("별", "") ?? "항목")}</th>
              {showSub && <th className={mergeClasses(styles.th, styles.num)}>인건비(제경비·기술료 포함)</th>}
              {showSub && <th className={mergeClasses(styles.th, styles.num)}>솔루션</th>}
              <th className={mergeClasses(styles.th, styles.num)}>{showBudget ? "예상 소진" : "금액"}</th>
              {showBudget && <th className={mergeClasses(styles.th, styles.num)}>승인 예산</th>}
              {showBudget && <th className={mergeClasses(styles.th, styles.num)}>잔여</th>}
              <th className={mergeClasses(styles.th, styles.num)}>{showBudget ? "집행률" : "비중"}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pct = showBudget ? (r.budget ? r.amount / r.budget : 0) : denominator ? r.amount / denominator : 0;
              return (
                <tr key={r.key} className={r.link ? styles.rowLink : undefined} onClick={r.link ? () => navigate(r.link!) : undefined}>
                  <td className={mergeClasses(styles.td, styles.nameCol)}>
                    <span className={styles.twoLine}>
                      <span className={styles.clip}>{r.label}</span>
                      {r.hint && <Caption1 className={styles.role}>{r.hint}</Caption1>}
                    </span>
                  </td>
                  {showSub && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(Math.round(r.sub?.labor ?? 0), masked)}</td>}
                  {showSub && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(Math.round(r.sub?.solution ?? 0), masked)}</td>}
                  <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(Math.round(r.amount), masked)}</td>
                  {showBudget && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(r.budget, masked)}</td>}
                  {showBudget && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency((r.budget ?? 0) - r.amount, masked)}</td>}
                  <td className={mergeClasses(styles.td, styles.num)}>{Math.round(pct * 100)}%</td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className={styles.total}>
                <td className={mergeClasses(styles.td, styles.nameCol, styles.nameTotal)}>합계</td>
                {showSub && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(Math.round(rows.reduce((s, r) => s + (r.sub?.labor ?? 0), 0)), masked)}</td>}
                {showSub && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(Math.round(rows.reduce((s, r) => s + (r.sub?.solution ?? 0), 0)), masked)}</td>}
                <td className={mergeClasses(styles.td, styles.num)}>
                  {fmtCurrency(Math.round(rowsTotal), masked)}
                </td>
                {showBudget && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(agg.budget, masked)}</td>}
                {showBudget && <td className={mergeClasses(styles.td, styles.num)}>{fmtCurrency(agg.budget - agg.total, masked)}</td>}
                <td className={mergeClasses(styles.td, styles.num)}>{showBudget ? `${Math.round(burn * 100)}%` : "100%"}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}
    </div>
  );
}
